import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/ampa-tms-cost — PROMPT 3
 * Google shows only investor sites for "ampa tms cost"; this is the first
 * patient-facing answer. Prices and accepted plans are confirmed at the
 * time of each free benefits check.
 */

const AMPA_TRADEMARK =
  "Ampa and Ampa One are trademarks of Ampa Inc. Neuro Wellness TMS Centers of America is an independent provider and is not owned by Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "How much does Ampa One TMS cost?",
    answer:
      "A standard Ampa One course is billed like other TMS and is commonly covered by insurance after prior authorization. Accelerated and one-day schedules are typically self-pay, and we quote your exact self-pay price at your free consultation. We verify your insurance benefits for free before you commit, so you know your real cost up front.",
  },
  {
    question: "Is Ampa One covered by insurance?",
    answer:
      "A standard Ampa One course for depression is commonly covered by insurance after prior authorization, because TMS for major depressive disorder is a covered benefit on most plans. Accelerated and one-day schedules are newer, so most plans do not cover them yet, and those are typically self-pay. Coverage varies by plan, and we verify your benefits for free.",
  },
  {
    question: "Does Medicare cover Ampa One?",
    answer:
      "Standard TMS for depression is a covered Medicare benefit when criteria are met (a depression diagnosis and prior antidepressant trials). Ampa One is an FDA-cleared TMS device, so a standard course billed as TMS may be covered by Medicare. Accelerated and one-day schedules are not separately covered by Medicare. We verify your specific plan and Medicare participation at your free consultation. Coverage varies by plan.",
  },
  {
    question: "Can I use my HSA or FSA for Ampa TMS?",
    answer:
      "Many patients use HSA or FSA funds for the patient-responsibility portion of TMS (copays, coinsurance, or self-pay). Because TMS for depression is a qualified medical expense, HSA/FSA funds can generally be applied; we confirm whether we accept HSA/FSA and which financing or payment plans we offer at your consultation. Check with your plan administrator.",
  },
  {
    question: "Is one-day TMS covered by insurance?",
    answer:
      "Not usually. One-day TMS compresses a full course into a single day, and that schedule is a newer, emerging protocol that most plans do not yet cover. The Ampa One device itself is FDA-cleared, but the one-day schedule is typically self-pay. A standard course is the most often covered. Coverage varies by plan.",
  },
  {
    question: "Why do TMS prices vary so much?",
    answer:
      "TMS prices vary by schedule (standard vs accelerated vs one-day), by whether insurance applies, and by what is included (mapping, sessions, clinical team, follow-up). A standard course is commonly covered by insurance. Accelerated and one-day schedules are typically self-pay because they are newer. The device brand matters less than whether the protocol is FDA-cleared and whether a psychiatrist supervises your care.",
  },
];

export const pageAmpaTmsCost = makeArticle({
  slug: "ampa-tms-cost",
  title:
    "Ampa One TMS Cost in Florida (2026): Price, Insurance & Payment Options",
  description:
    "How much Ampa One TMS costs in Florida: standard course, accelerated, and one-day prices, insurance coverage, Medicare, HSA/FSA, and financing. Free benefits verification.",
  canonical: "/learn/ampa-tms-cost",
  h1: "How Much Does Ampa One TMS Cost?",
  breadcrumbLabel: "Ampa One TMS Cost",
  shortAnswer:
    "A standard Ampa One course is billed like other TMS and is commonly covered by insurance after prior authorization. Accelerated and one-day schedules are typically self-pay, and we quote your exact self-pay price at your free consultation. We verify your insurance benefits for free before you commit, so you know your real cost up front. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation.",
  keyTakeaways: [
    "A standard Ampa One course is commonly covered by insurance after prior authorization.",
    "Accelerated and one-day schedules are newer and typically self-pay.",
    "TMS for depression is billed with CPT codes 90867–90869; plans usually require a depression diagnosis, failed antidepressant trials, and prior authorization.",
    "HSA/FSA funds can generally be applied to the patient-responsibility portion.",
    "Fewer treatment days can mean less time off work, travel, and childcare.",
    "We verify your benefits for free before you commit.",
  ],
  sections: [
    {
      heading: "Quick answer: what Ampa One TMS costs",
      paragraphs: [
        "A standard Ampa One course is billed like other TMS for depression and is commonly covered by insurance after prior authorization. Accelerated and one-day schedules are newer, emerging protocols, so most plans do not cover them yet, and those are typically self-pay. We quote your exact self-pay price at your free consultation. The most useful number is your real out-of-pocket cost after benefits, which we verify for free before you commit.",
      ],
    },
    {
      heading: "Price by schedule at Neuro Wellness",
      paragraphs: [
        "The table below shows how each schedule is typically billed. We confirm your exact price after a free benefits check.",
      ],
      table: {
        headers: ["Schedule", "Typical price at Neuro Wellness", "Insurance"],
        rows: [
          [
            "Standard course",
            "Commonly covered by insurance after prior authorization",
            "Commonly covered after prior authorization",
          ],
          [
            "Accelerated / 2-day",
            "Typically self-pay (quote at consultation)",
            "Typically self-pay",
          ],
          [
            "One-Day",
            "Typically self-pay (quote at consultation)",
            "Typically self-pay",
          ],
        ],
      },
    },
    {
      heading: "How insurance works for TMS",
      paragraphs: [
        "TMS for depression is billed with CPT codes 90867 (initial treatment), 90868 (subsequent treatment), and 90869 (final treatment). Plans usually require a major depressive disorder diagnosis, documented failed antidepressant trials, and prior authorization before approving TMS. A standard Ampa One course meets these requirements the same way any FDA-cleared TMS does.",
        "Accelerated and one-day schedules are newer, so most plans do not have a payment policy for them yet, which is why they are typically self-pay. Coverage varies by plan. We verify your benefits for free so you know what applies before you start.",
      ],
    },
    {
      heading: "Medicare, VA, and the plans we accept",
      paragraphs: [
        "Standard TMS for depression is a covered Medicare benefit when criteria are met. We confirm your specific Medicare and plan participation at your free consultation. For commercial plans and the VA, see [[Cost & Insurance|/cost-insurance]] and [[SAINT® TMS Insurance Coverage|/saint-tms-insurance-coverage]]. We verify your specific plan for free.",
      ],
    },
    {
      heading: "HSA/FSA, financing, and payment plans",
      paragraphs: [
        "Many patients use HSA or FSA funds for the patient-responsibility portion of TMS (copays, coinsurance, or self-pay), because TMS for depression is a qualified medical expense. We confirm whether we accept HSA/FSA and which financing or payment plans we offer at your consultation. Financing is available case by case.",
      ],
    },
    {
      heading: "Why the total cost can be lower than it looks",
      paragraphs: [
        "An accelerated or one-day schedule means fewer days off work, fewer trips to the clinic, and less childcare and travel — which can lower the real total cost of treatment compared with a standard six-week course. For patients traveling from outside South Florida, see [[Ampa One TMS in Florida|/ampa-tms-florida]].",
      ],
    },
    {
      heading: "Free benefits check",
      paragraphs: [
        "Before you commit to any schedule, we verify your insurance benefits for free so you know your real out-of-pocket cost. Call 954-827-9793 or request a free consultation.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "American Medical Association. CPT codes 90867–90869 — Transcranial magnetic stimulation (TMS) treatment.",
    },
    {
      citation:
        "Ampa Inc. Ampa One — FDA-cleared transcranial magnetic stimulation system for major depressive disorder.",
      url: "https://www.ampahealth.com/",
    },
  ],
  relatedLinks: [
    {
      title: "Ampa One™ TMS in Florida",
      href: "/ampa",
      description: "Camera-guided, accelerated depression treatment",
    },
    {
      title: "One-Day TMS in Florida",
      href: "/one-day-tms",
      description: "The Ampa One Day protocol explained",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "What the 5-day fMRI-guided protocol costs",
    },
    {
      title: "Cost & Insurance",
      href: "/cost-insurance",
      description: "Benefits verification and payment options",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer-what-ampa-one-tms-costs" },
    {
      label: "Price by schedule",
      href: "#price-by-schedule-at-neuro-wellness",
    },
    { label: "How insurance works", href: "#how-insurance-works-for-tms" },
    { label: "Medicare & plans", href: "#medicare-va-and-the-plans-we-accept" },
    {
      label: "HSA/FSA & financing",
      href: "#hsafsa-financing-and-payment-plans",
    },
    {
      label: "Total cost",
      href: "#why-the-total-cost-can-be-lower-than-it-looks",
    },
    { label: "Free benefits check", href: "#free-benefits-check" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation. Ampa One is FDA-cleared for major depressive disorder in adults who have not improved enough on antidepressant medication. Common side effects include mild scalp discomfort and headache that usually fade. If you are in crisis, call or text 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Check your Ampa One benefits, free",
    primaryText: "Verify my insurance",
    primaryHref: "/check-insurance-options",
    secondaryText: "Request a free consultation",
    secondaryHref: "/ampa",
    phone: "954-827-9793",
  },
});
